
import React, { useState } from 'react';
import { X, ShoppingBag, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart, CartItem } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { QuantitySelector } from '../offers/QuantitySelector';
import { useCategories } from '@/hooks/categories/useCategories';
import { IncludedServicesDisplay } from '@/components/devis/IncludedServicesDisplay';
import { AddServicesModal } from './AddServicesModal';
import { formatPrixIndicatif } from '@/utils/formatCurrency';

interface CartSidebarProps {
  onClose: () => void;
}

const CartItemRow: React.FC<{ item: CartItem; category?: any }> = ({ item, category }) => {
  const { removeItem } = useCart();
  
  const handleRemove = () => {
    removeItem(item.id);
  };
  
  return (
    <motion.div 
      className="flex items-start gap-3 p-3 border-b border-gray-100 last:border-0"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
          {item.title || 'Titre manquant'}
        </h4>
        <p className="text-sm text-gray-600 mb-1">
          {formatPrixIndicatif(item.price)}
        </p>
        
        {/* Affichage des prestations incluses pour les évènements */}
        <IncludedServicesDisplay category={category} compact />
        
        {item.level && (
          <p className="text-xs text-gray-500 mt-1">Niveau: {item.level}</p>
        )}
        {item.type && (
          <p className="text-xs text-gray-400 mt-1">Type: {item.type}</p>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1 h-auto"
        onClick={handleRemove}
        aria-label={`Retirer ${item.title || 'cet item'}`}
      >
        <X className="h-4 w-4" />
      </Button>
    </motion.div>
  );
};

const QuantifiableOfferRow: React.FC<{ offer: any }> = ({ offer }) => {
  const { removeQuantifiableOffer, updateOfferQuantity } = useCart();
  
  // Fonction pour déterminer l'unité selon le type d'offre
  const getUnitText = (title: string) => {
    const offerTitle = title.toLowerCase();
    if (offerTitle.includes('serveur')) return 'serveur(s)';
    if (offerTitle.includes('barman')) return 'barman(s)';
    if (offerTitle.includes('hôtesse')) return 'hôtesse(s)';
    if (offerTitle.includes('chef')) return 'chef(s)';
    if (offerTitle.includes('animateur')) return 'animateur(s)';
    return 'unité(s)';
  };
  
  const handleRemove = () => {
    removeQuantifiableOffer(offer.offerId);
  };
  
  return (
    <motion.div 
      className="flex items-start gap-3 p-3 border-b border-gray-100 last:border-0 bg-blue-50/30"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
            {offer.title}
          </h4>
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
            Quantifiable
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-2">
          {offer.basePrice}€ {offer.priceUnit} × {offer.quantity} {getUnitText(offer.title)} = {offer.totalPrice}€
        </p>
        {offer.level && (
          <p className="text-xs text-gray-500 mb-2">
            Niveau: {offer.level.charAt(0).toUpperCase() + offer.level.slice(1)}
          </p>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Quantité:</span>
          <QuantitySelector
            quantity={offer.quantity}
            onQuantityChange={(quantity) => updateOfferQuantity(offer.offerId, quantity)}
            min={1}
            max={20}
            size="sm"
          />
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1 h-auto"
        onClick={handleRemove}
        aria-label={`Retirer ${offer.title}`}
      >
        <X className="h-4 w-4" />
      </Button>
    </motion.div>
  );
};

export const CartSidebar: React.FC<CartSidebarProps> = ({ onClose }) => {
  const { items, quantifiableOffers, itemCount, clearCart, getAllCartItemsForQuote } = useCart();
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [showAddServicesModal, setShowAddServicesModal] = useState(false);
  
  const handleClearCart = () => {
    clearCart();
  };

  const handleFinalizeRequest = () => {
    setShowAddServicesModal(true);
  };

  const handleAddServices = () => {
    setShowAddServicesModal(false);
    onClose();
    navigate('/nos-offres');
  };

  const handleContinueWithoutServices = () => {
    setShowAddServicesModal(false);
    onClose();
    navigate('/devis');
  };
  
  if (itemCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-8 text-center">
        <ShoppingBag className="h-16 w-16 text-gray-300 mb-4" />
        <h3 className="font-medium text-gray-900 mb-2">Votre panier est vide</h3>
        <p className="text-sm text-gray-600 mb-4">
          Découvrez nos évènements et ajoutez-les à votre panier pour commencer.
        </p>
        <Button 
          variant="outline" 
          onClick={() => {
            onClose();
            navigate('/univers-particuliers');
          }}
          className="text-primary border-primary hover:bg-primary hover:text-white"
        >
          Découvrir nos évènements
        </Button>
      </div>
    );
  }

  // Calculer le total estimé en incluant les offres quantifiables
  const allCartItems = getAllCartItemsForQuote();
  const totalEstimated = allCartItems.reduce((sum, item) => sum + item.totalPrice, 0);

  return (
    <div className="flex flex-col h-full">
      {/* Header with item count and clear button */}
      <div className="flex items-center justify-between py-4 border-b border-gray-100">
        <span className="text-sm font-medium text-gray-700">
          {itemCount} élément{itemCount > 1 ? 's' : ''} sélectionné{itemCount > 1 ? 's' : ''}
        </span>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-xs text-gray-500 hover:text-red-600"
          onClick={handleClearCart}
        >
          Tout vider
        </Button>
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence>
          {/* Afficher les items standards */}
          {items.map((item) => {
            const category = categories?.find(cat => cat.id === item.id);
            return (
              <CartItemRow key={item.id} item={item} category={category} />
            );
          })}
          
          {/* Afficher les offres quantifiables */}
          {quantifiableOffers.map((offer) => (
            <QuantifiableOfferRow key={offer.offerId} offer={offer} />
          ))}
        </AnimatePresence>
      </div>

      {/* Footer with total and action button */}
      <div className="border-t border-gray-100 pt-4 mt-4">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-gray-900">Estimation indicative</span>
            <span className="font-semibold text-primary">
              {formatPrixIndicatif(totalEstimated)}
            </span>
          </div>
          <p className="text-xs text-gray-500">
            Estimation à partir de nos tarifs de base. Votre chef de projet dédié l'affine dans une proposition sur-mesure.
          </p>
        </div>
        
        <Button 
          onClick={handleFinalizeRequest}
          className="w-full bg-primary hover:bg-primary-hover text-white font-medium"
          size="lg"
        >
          Finaliser ma demande
        </Button>
      </div>

      <AddServicesModal
        open={showAddServicesModal}
        onOpenChange={setShowAddServicesModal}
        onAddServices={handleAddServices}
        onContinueWithoutServices={handleContinueWithoutServices}
      />
    </div>
  );
};
